import { FormEvent, useContext, useEffect, useState } from "react";
import { PAGE_ROUTES } from "~/constants/pages";
import { setCookie } from "~/helpers/cookie";
// import { isNonEmptyObject } from '~/helpers/objectHelper';
// import { isNonEmptyString } from '~/helpers/stringHelpers';
// import { useRunServiceability } from '~/hooks/useRunServiceability';
import { FuelContext } from "~/providers/FuelProvider";
import { Address, ServiceabilityResponse } from "~/types/address";
import { useDispatch, useSelector } from "react-redux";
import { resetAddressRoute, setAddress, setServiceabilityResults, setUnservReason } from "~/redux/actions/address";
import { ReduxState } from "~/types/redux";
import useFetch from "~/hooks/useFetch";
import { DEFAULT_API_VERSION } from "~/constants/config/common";
import { removeACP, setPageLoading, setResume, setCookiePass } from "~/redux/actions/common";
import { Tracking } from "~/types/common";
import { DEFAULT_ERROR_MESSAGE, SERVICEABILITY_API_VERSION } from "~/constants/config/common";
import { setNotifications } from "~/redux/actions/global";
import Cookie from "js-cookie";
import { removeFrontierCookie } from "~/helpers/common";
import monarch from "../../monarch/api";

interface ReturnType {
  apiVersion: string;
  selectedAddress: Address;
  installAddress: Address | Record<string, unknown>;
  setSelectedAddress: (address: Address) => void;
  handleFormSubmit: (event: FormEvent<HTMLFormElement>) => void;
  handleInputChange: (event: FormEvent<HTMLInputElement>) => void;
  buttonState: {
    disabled: boolean;
    loading: boolean;
  };
  tracking: Tracking;
  addressHistory: string[];
  submitted: boolean;
}

interface ServiceType {
  address?: Address;
  addressKey?: string;
  env?: string;
  controlNumber?: string;
  formattedAcpAddress?: Address;
  referenceId?: string;
  isVerizonAffiliate?: boolean;
}

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
declare const window: any;

const useAddressCheck = (): ReturnType => {
  const [submitted, setSubmitted] = useState(false);
  const { resume, address, tracking, acp, addressHistory, landingPage, unservReason, firebase } = useSelector(
    (store: ReduxState) => store
  );

  // this is the address used to run serviceability
  const defaultAddress = {
    addressKey: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    zip4: "",
    parsedAddress: {},
  };
  const [selectedAddress, setSelectedAddress] = useState<Address>(defaultAddress);
  const [serviceabilityRan, setServiceabilityRan] = useState(false);
  const { router, trackEvent } = useContext(FuelContext);
  const dispatch = useDispatch();

  // const { apiVersion } = monarch;

  const apiVersion = (monarch?.apiVersion as string) || DEFAULT_API_VERSION;
  const serviceabilityApiVersion = SERVICEABILITY_API_VERSION;
  const {
    data,
    loading: runServiceabilityLoading,
    fetch: runServiceability,
  } = useFetch<ServiceabilityResponse>(`${serviceabilityApiVersion}/serviceability`, {
    method: "post",
  });

  const runService = async ({
    address,
    addressKey,
    env,
    controlNumber,
    formattedAcpAddress,
    referenceId,
    isVerizonAffiliate,
  }: ServiceType) => {
    if (unservReason) {
      dispatch(setUnservReason(""));
    }
    const isVerizonIncluded = firebase?.leadshare ? JSON.parse(firebase.leadshare._value)?.enabled : false;
    let body = {};
    if (address) {
      body = {
        address: {
          addressKey: address.addressKey,
          parentKey: address.parentKey,
          address: address.address,
          address2: address.address2,
          city: address.city,
          state: address.state,
          postalCode: address.zip,
          zip: address.zip,
          zip4: address.zip4,
          env: address.env,
          controlNumber: address.controlNumber,
          isParent: address.isParent,
          inFootprint: address.inFootprint,
        },
        isVerizonIncluded: isVerizonIncluded,
        rawUserString: address.rawUserInput,
      };
    } else if (env && controlNumber) {
      body = {
        addressKey,
        e: env,
        c: controlNumber,
        isVerizonIncluded: isVerizonIncluded,
        formattedAcpAddress: {
          address: formattedAcpAddress?.address,
          address2: formattedAcpAddress?.address2,
          city: formattedAcpAddress?.city,
          state: formattedAcpAddress?.state,
          zip: formattedAcpAddress?.zip,
        },
      };
    } else if (referenceId) {
      body = {
        isVerizonIncluded: isVerizonIncluded,
        referenceId: referenceId,
      };
    }
    if (body && Object.keys(body)?.length === 0) {
      return;
    }
    try {
      const serviceabilityResult = await runServiceability({ body: JSON.stringify(body) });
      dispatch(
        setServiceabilityResults({
          response: serviceabilityResult,
          router,
          isVerizonAffiliate,
        })
      );
      removeFrontierCookie();
      return serviceabilityResult;
    } catch (error) {
      dispatch(setPageLoading(false));
      dispatch(setNotifications([{ type: "error", message: DEFAULT_ERROR_MESSAGE }]));
      return { success: false, error: DEFAULT_ERROR_MESSAGE };
    }
  };

  const installZip = address?.install?.zip ?? "none";

  useEffect(() => {
    // set zip cookie for preamp
    setCookie("zip", installZip, { otGroup: "C0004" });
  }, [installZip]);

  useEffect(() => {
    if (landingPage?.persisted === false) {
      trackEvent({
        action: "elementViewed",
        data: {
          elementType: "CART STATE",
          location: "address_check_page",
          position: "PERSISTED FROM MLP",
          text: "DIFF ADDRESS",
        },
      });
    }
  }, [landingPage?.persisted]);

  // ACP or Cookie pass flow.
  useEffect(() => {
    let timeoutId = null;

    if (!Cookie.get("frontierSiteDetailPredictive")) dispatch(setCookiePass(false));

    const acpFlow = acp?.env && acp?.controlNumber;
    if (acpFlow) {
      dispatch(setResume("enabled", false));
      dispatch(setPageLoading(true));
      timeoutId = setTimeout(() => {
        dispatch(setPageLoading(false));
      }, 5000);
    }

    if (acpFlow) {
      clearTimeout(timeoutId);
      runService(acp);
      dispatch(removeACP());
    } else if (address?.install?.address && !serviceabilityRan) {
      setServiceabilityRan(true);
      const popup = window.sessionStorage.getItem("seenResumeModal");
      clearTimeout(timeoutId);
      // This is the use case where the address is external (e.g in frontier.com)
      if (!data && popup != "true") {
        runService({ address: address?.install });
      }
      dispatch(removeACP());
    }

    // If the component unmounts, clear the timeout
    return () => {
      clearTimeout(timeoutId);
    };
  }, [address?.install?.address]);

  useEffect(() => {
    if (firebase !== null && typeof window !== "undefined" && !runServiceabilityLoading) {
      const urlParams = new URLSearchParams(window.location?.search);
      const customAddress = urlParams.get("address");
      const customCity = urlParams.get("city");
      const customState = urlParams.get("state");
      const customPostalCode = urlParams.get("postalCode");
      const customAddress2 = urlParams.get("address2");
      const env = urlParams.get("env");
      const controlNumber = urlParams.get("controlNumber");
      if (customAddress !== null && !serviceabilityRan) {
        // Run serviceability with custom address
        const updatedAddress = {
          addressKey: "",
          address: customAddress?.trim(),
          address2: customAddress2 || "",
          city: customCity?.trim(),
          state: customState?.trim(),
          zip: customPostalCode?.trim(),
          zip4: "",
        };
        setSelectedAddress(updatedAddress);
        setServiceabilityRan(true);
        runService({ address: updatedAddress });
      } else if (env && controlNumber && !serviceabilityRan) {
        // Run serviceability with environment and control number
        setServiceabilityRan(true);
        runService({ env, controlNumber });
      }

      const referenceId = urlParams.get("referenceId")?.trim();
      const affiliateKey = urlParams.get("affiliateKey")?.trim();
      const affiliateConfig = affiliateKey && JSON.parse(firebase.affiliateConfig._value)[affiliateKey];
      const affiliateName = affiliateConfig?.name;
      const isVerizonAffiliate = referenceId && affiliateName === "CL-VZ-LEADS";
      if (referenceId && !serviceabilityRan) {
        // Run serviceability with referenceId
        runService({ referenceId, isVerizonAffiliate });
      }
    }
  }, [firebase?.affiliateConfig]);

  // Prefetch the next page chunk.
  useEffect(() => {
    if (resume.cartPage !== "/submit") router.prefetch(PAGE_ROUTES.PLAN_PACKAGE);
  }, []);

  // Event Handlers
  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    setSubmitted(true);
    event.preventDefault();
    dispatch(resetAddressRoute());
    // Hides error banner when running new address
    dispatch(setNotifications());

    trackEvent({
      action: "elementClicked",
      data: {
        text: "Check availability",
      },
      event,
    });
    dispatch(setAddress("install", selectedAddress));
    // Check if address is in AG states (NV, OH, MN, PA) and set cookie
    const AGStates = ["NV", "OH", "MN", "PA"];
    const isAGState = selectedAddress.state && AGStates.includes(selectedAddress.state);
    if (isAGState) {
      setCookie("isAGState", "true");
    }

    setServiceabilityRan(true);
    runService({ address: selectedAddress });
  };

  const handleInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;

    setSelectedAddress({
      ...selectedAddress,
      [name]: value,
    });
  };

  return {
    apiVersion,
    selectedAddress,
    installAddress: address?.install || {},
    setSelectedAddress,
    handleFormSubmit,
    handleInputChange,
    buttonState: {
      disabled: !selectedAddress.addressKey,
      loading: runServiceabilityLoading,
    },
    tracking,
    addressHistory,
    submitted,
  };
};

export default useAddressCheck;
